import React, { FC, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import tw from 'twin.macro'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { SelectMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { Divider, Typography } from '@mui/material'
import { GSIcon } from 'src/components/shared/GSIcon'
import { showToast } from 'src/utils/toast'
import { Customer } from 'src/interfaces/customer'
import { Market } from 'src/interfaces/market'
import { OnboardingTemplate } from 'src/interfaces/onboarding'
import { getUser } from 'src/selectors/user'
import { getOnBoardingStepsNames } from '../InviteWorkersUtils'

interface InviteWorkersViaLinkFormContentProps {
  partner: Customer | null
  onboardingTemplates: OnboardingTemplate[]
  markets: Market[]
}

interface InviteWorkersViaLinkFormValues {
  collectDataTemplateId: string
  partnerMarketId: string
}

export const handleCopyInviteLink = (link: string) => {
  navigator.clipboard.writeText(link)
  showToast('Link copied!', { variant: 'success' })
}

export const InviteWorkersViaLinkFormContent: FC<InviteWorkersViaLinkFormContentProps> = ({
  partner,
  onboardingTemplates,
  markets,
}) => {
  const { values, isValid } = useFormikContext<InviteWorkersViaLinkFormValues>()
  const user = useSelector(getUser)

  const generateInviteLink = (
    partnerName: string,
    partnerMarketId: string,
    onboardingTemplateId: string,
    initiatorId?: string,
  ) => {
    const link = `${process.env.REACT_APP_ONBOARDING_CLIENT_URL}/client/${partnerName}/sign-up?partnerMarketId=${partnerMarketId}&onboardingTemplateId=${onboardingTemplateId}`

    return initiatorId ? `${link}&initiatorId=${initiatorId}` : link
  }

  const inviteLink =
    partner && isValid && values.partnerMarketId && values.collectDataTemplateId
      ? generateInviteLink(
          partner.name,
          values.partnerMarketId,
          values.collectDataTemplateId,
          user?.id,
        )
      : null

  const stepNames = useMemo(() => {
    return getOnBoardingStepsNames(onboardingTemplates, values.collectDataTemplateId)
  }, [values.collectDataTemplateId, onboardingTemplates])

  const showOnboardingSteps = Boolean(values.collectDataTemplateId && stepNames.trim())

  const selectedTemplate = useMemo(
    () => onboardingTemplates.find((t) => t.id === values.collectDataTemplateId),
    [values.collectDataTemplateId, onboardingTemplates],
  )

  return (
    <div data-testid="contractors-onboarding-invite-link-form">
      <div css={tw`px-4 pt-5`}>
        <div css={tw`flex min-w-0 gap-4 mb-5 border-0`}>
          <div css={tw`min-w-0 flex-1`}>
            <FormikSelect
              name="collectDataTemplateId"
              label="Onboarding flow"
              placeholder="Select flow"
              IconComponent={(props) => <SelectMinor width={20} height={20} {...props} />}
              options={onboardingTemplates.map((item) => ({
                label: item.name,
                value: item.id,
              }))}
              inputProps={{
                'data-testid': 'contractors-onboarding-invite-link-flow-select-input',
              }}
            />
          </div>
          <div css={tw`min-w-0 flex-1`}>
            <FormikSelect
              name="partnerMarketId"
              label="Market"
              placeholder="Select market"
              IconComponent={(props) => <SelectMinor width={20} height={20} {...props} />}
              options={markets.map((item) => ({
                label: item.title,
                value: item.id,
              }))}
              inputProps={{
                'data-testid': 'contractors-onboarding-invite-link-market-select-input',
              }}
            />
          </div>
        </div>
        <Divider css={tw`mt-4 w-full border-[#EDEDED]`} />
        {showOnboardingSteps && (
          <div css={tw`flex mt-3 p-0`}>
            <div css={tw`w-1 bg-[#8A2BE2] rounded mr-0`} />
            <div css={tw`pl-3 flex-1`}>
              <Typography css={tw`text-sm font-semibold mb-1`}>
                {selectedTemplate?.name || 'Selected Flow'} Step(s)
              </Typography>
              <Typography css={tw`text-sm text-[#847E96]`}>{stepNames}</Typography>
            </div>
          </div>
        )}
        {showOnboardingSteps && <Divider css={tw`mt-4 w-full border-[#EDEDED]`} />}
      </div>
      <div css={tw`flex-col items-center border-0 mt-5 mb-8 pl-4 pr-4`}>
        <div css={tw`flex items-center w-full`}>
          <div css={tw`flex-grow mr-4`}>
            <Typography css={tw`text-sm font-semibold text-[#847E96] mt-0`}>
              Invitation link
            </Typography>
            <Typography css={tw`text-sm text-black break-all`}>
              {inviteLink ? inviteLink : 'Link will appear here'}
            </Typography>
          </div>
          <Button
            variant="contained"
            size="small"
            onClick={() => inviteLink && handleCopyInviteLink(inviteLink)}
            data-testid="contractors-onboarding-invite-link-copy-btn"
            rightIcon={
              <GSIcon
                iconName="gs_square_behind_square_light"
                css={tw`self-center`}
                width={20}
                height={20}
              />
            }
            disabled={!inviteLink}
          >
            Copy link
          </Button>
        </div>
        <Divider css={tw`mt-4 w-full border-[#EDEDED]`} />
      </div>
    </div>
  )
}
